import { locale } from "./locale.js";

/**
 * Every country a wine may come from, and what to call it in Swedish — or in
 * English, since 2026-10-07.
 *
 * ## Why the list is all of them
 *
 * Until `9.10` the registration form offered eight countries, hard-coded in
 * `WineForm.tsx`, because the cellar held eight. That is the same mistake the
 * purchase source made in the schema and for the same reason: a closed list
 * built from what has been bought so far cannot represent the next bottle, and
 * the person holding it then picks the nearest wrong value. `Wine.countryCode`
 * has always been a free ISO-3166-1 alpha-2 column — nothing below the
 * interface was ever closed — so opening the picker takes no migration.
 *
 * The codes are the ISO countries of `flag-icons`, which is also where the
 * flags come from, so a country that can be chosen is a country whose flag the
 * build has. Written down rather than generated into the bundle at build: the
 * list changes when the world does, which is roughly never, and a generated
 * module would have to exist before anything typechecks — one more step in CI
 * and one more way for a green laptop to push a red commit.
 *
 * ## Why the names are not in `sv.json`
 *
 * `Intl.DisplayNames` is in every browser this application supports and it
 * knows all of them in Swedish. Two hundred and forty-nine names in the
 * translation bundle would be two hundred and forty-nine strings nobody wrote,
 * nobody reviews and nobody can improve — they are data about the world rather
 * than this application's own words, which is the line `sv.json` draws. All
 * eight names it used to hold came back identical from the platform, so
 * nothing was lost in the exchange.
 */

/** The ISO-3166-1 alpha-2 countries, as `flag-icons` lists them. */
export const COUNTRY_CODES: readonly string[] = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BV",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GS",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HM",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TF",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UM",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW",
];

/**
 * One set of names per language, made the first time it is asked for. The
 * platform holds the names, so English cost no strings either.
 */
const NAMES = new Map<string, Intl.DisplayNames>();

function names(): Intl.DisplayNames {
  const at = locale();
  let found = NAMES.get(at);
  if (found === undefined) {
    found = new Intl.DisplayNames([at], { type: "region", fallback: "code" });
    NAMES.set(at, found);
  }
  return found;
}

/**
 * A country code's name in the interface's language, falling back to the code
 * itself.
 *
 * The fallback is not decoration: a cellar imported from a spreadsheet can
 * hold a code this list does not, and a row that said `undefined` where a
 * country should be would be worse than one that says `XK`.
 */
export function countryName(code: string): string {
  try {
    return names().of(code) ?? code;
  } catch {
    // `of` throws on anything that is not a well-formed region code, which a
    // hand-edited query string can be.
    return code;
  }
}

/**
 * Every country, for a picker: **the ones the reader's cellars hold first,
 * then the rest — each group in alphabetical order**, Swedish or English as
 * the names are. Asked for by
 * the owner on 2026-09-29. Until then the rest came in the order of their
 * codes, which put Tyskland under D and Schweiz under C, and the cellar's own
 * came in the order they were counted.
 *
 * `preferred` is how many lead the list, so the picker can draw a line under
 * them: it is where the eye stops looking for France and starts looking for
 * Georgien.
 */
export function orderCountries(held: readonly string[]): {
  options: string[];
  preferred: number;
} {
  const collator = new Intl.Collator(locale());
  const byName = (a: string, b: string) =>
    collator.compare(countryName(a), countryName(b));
  const first = [...new Set(held)]
    .filter((code) => COUNTRY_CODES.includes(code))
    .sort(byName);
  const rest = COUNTRY_CODES.filter((code) => !first.includes(code)).sort(byName);
  return { options: [...first, ...rest], preferred: first.length };
}
